import { ChangeEvent, FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "./styles.module.css";
import { Alert, Button, Collapse, TextField, Typography } from "@mui/material";
import useSignup from "../../services/useSignup";
import AddressFieldset from "../../components/AddressFieldset";
import { IAddress } from "../../types/seed";
import { formatAddress } from "../../utils/addressUtils";
import validatePhone from "../../utils/validatePhone";
import phoneToE164 from "../../utils/phoneToE164";
import validateEmail from "../../utils/validateEmail";
import { getApiErrorMessage, getApiFieldErrors } from "../../utils/apiError";

type FormField =
  | "email"
  | "name"
  | "password"
  | "passwordConfirmation"
  | "phone";
type FieldErrors = Partial<Record<FormField | "address", string>>;

const Signup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    address_obj: null as IAddress | null,
    email: "",
    name: "",
    password: "",
    passwordConfirmation: "",
    phone: "",
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const { error, isError, isLoading, isSuccess, mutate, reset } = useSignup();

  const handleChange =
    (key: FormField) => (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setForm((currentForm) => ({
        ...currentForm,
        [key]: value,
      }));
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        [key]: undefined,
      }));
      if (isError) reset();
    };

  const handleAddressObjChange = (address_obj: IAddress | null) => {
    setForm((currentForm) => ({
      ...currentForm,
      address_obj,
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      address: undefined,
    }));
  };

  const validateForm = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (!form.name.trim()) errors.name = "Informe seu nome.";
    const emailError = validateEmail(form.email);
    if (emailError) errors.email = emailError;
    const phoneError = validatePhone(form.phone);
    if (phoneError) errors.phone = phoneError;
    if (!form.password) errors.password = "Informe uma senha.";
    if (!form.passwordConfirmation) {
      errors.passwordConfirmation = "Confirme sua senha.";
    } else if (form.password !== form.passwordConfirmation) {
      errors.passwordConfirmation = "As senhas não coincidem.";
    }
    const address = form.address_obj;
    if (
      !address ||
      !address.postal_code ||
      !address.street ||
      !address.number ||
      !address.neighborhood ||
      !address.city ||
      !address.state
    ) {
      errors.address = "Preencha todos os campos obrigatórios do endereço.";
    }
    return errors;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Converte o telefone para E.164 antes de enviar
    let phoneE164 = form.phone;
    try {
      phoneE164 = phoneToE164(form.phone);
    } catch (error) {
      // Se não conseguir converter, mantém o valor original
      // A validação do backend vai capturar o erro
    }

    mutate(
      {
        address: form.address_obj ? formatAddress(form.address_obj) : "",
        address_obj: form.address_obj,
        email: form.email,
        name: form.name,
        password: form.password,
        phone: phoneE164,
      },
      {
        onSuccess() {
          setTimeout(() => {
            navigate("/login", { replace: true });
          }, 2000);
        },
        onError(error) {
          const apiErrors = getApiFieldErrors(error);
          setFieldErrors({
            email: apiErrors.email,
            name: apiErrors.name,
            password: apiErrors.password,
            phone: apiErrors.phone,
            address: apiErrors.address || apiErrors.address_obj,
          });
        },
      },
    );
  };

  const closeAlert = () => {
    reset();
    setFieldErrors({});
  };

  const signupErrorMessage = getApiErrorMessage(
    error,
    "Não foi possível concluir o cadastro. Revise os campos destacados e tente novamente.",
  );

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.innerContainer}>
          <Typography variant="h5" component="h1">
            Olá, parceira!
          </Typography>
          <Typography variant="body1" component="h1">
            Cadastre-se para começar a montar sua cesta da semana!
          </Typography>
          <form className={styles.form} noValidate onSubmit={handleSubmit}>
            <Collapse in={isError}>
              <Alert severity="error" onClose={closeAlert}>
                {signupErrorMessage}
              </Alert>
            </Collapse>
            <Collapse in={isSuccess}>
              <Alert severity="success" onClose={closeAlert}>
                Cadastro realizado! Você será direcionada para entrar na sua
                conta.
              </Alert>
            </Collapse>
            <TextField
              autoComplete="name"
              autoFocus
              error={!!fieldErrors.name}
              fullWidth
              helperText={fieldErrors.name}
              label="Nome"
              onChange={handleChange("name")}
              required
              value={form.name}
            />
            <TextField
              autoComplete="tel"
              error={!!fieldErrors.phone}
              fullWidth
              helperText={fieldErrors.phone}
              label="Telefone"
              onChange={handleChange("phone")}
              required
              value={form.phone}
            />
            {fieldErrors.address && (
              <Alert severity="error">{fieldErrors.address}</Alert>
            )}
            <AddressFieldset
              address={form.address_obj}
              onChange={handleAddressObjChange}
            />
            <TextField
              autoComplete="email"
              error={!!fieldErrors.email}
              fullWidth
              helperText={fieldErrors.email}
              label="E-mail"
              onChange={handleChange("email")}
              required
              value={form.email}
            />
            <TextField
              autoComplete="new-password"
              error={!!fieldErrors.password}
              fullWidth
              helperText={fieldErrors.password}
              label="Senha"
              onChange={handleChange("password")}
              required
              type="password"
              value={form.password}
            />
            <TextField
              autoComplete="new-password"
              error={!!fieldErrors.passwordConfirmation}
              fullWidth
              helperText={fieldErrors.passwordConfirmation}
              label="Confirmar senha"
              onChange={handleChange("passwordConfirmation")}
              required
              type="password"
              value={form.passwordConfirmation}
            />
            <Button
              disabled={isLoading || isSuccess}
              type="submit"
              variant="contained"
            >
              {isLoading ? "Cadastrando..." : "Cadastrar"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
